import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import GalleryManager from "@/components/admin/GalleryManager";
import BeforeAfterManager from "@/components/admin/BeforeAfterManager";
import ReviewsManager from "@/components/admin/ReviewsManager";
import ContactsManager from "@/components/admin/ContactsManager";
import SiteInfoManager from "@/components/admin/SiteInfoManager";
import ServicesManager from "@/components/admin/ServicesManager";
import { LogOut, Home } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("gallery");

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand-100">
      <header className="border-b border-sand-300 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sage font-bold">Admin Bereich</p>
            <h1 className="font-display text-2xl text-stone-900">Verwaltung</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-600 hidden sm:block" data-testid="admin-email">{user?.email}</span>
            <Link to="/" data-testid="admin-view-site">
              <Button variant="outline" size="sm" className="border-sand-300 text-stone-900 hover:bg-sand-200">
                <Home className="w-4 h-4 mr-2" /> Webseite
              </Button>
            </Link>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              data-testid="admin-logout-button"
              className="border-sand-300 text-stone-900 hover:bg-sand-200"
            >
              <LogOut className="w-4 h-4 mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-sand-300 rounded-sm h-auto p-1 flex flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Galerie</TabsTrigger>
            <TabsTrigger value="before-after" data-testid="tab-before-after" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Vorher / Nachher</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Leistungen</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Bewertungen</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Anfragen</TabsTrigger>
            <TabsTrigger value="site" data-testid="tab-site-info" className="rounded-sm data-[state=active]:bg-stone-900 data-[state=active]:text-sand-100 px-5 py-2">Betrieb</TabsTrigger>
          </TabsList>

          <TabsContent value="gallery" className="mt-8"><GalleryManager /></TabsContent>
          <TabsContent value="before-after" className="mt-8"><BeforeAfterManager /></TabsContent>
          <TabsContent value="services" className="mt-8"><ServicesManager /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><ReviewsManager /></TabsContent>
          <TabsContent value="contacts" className="mt-8"><ContactsManager /></TabsContent>
          <TabsContent value="site" className="mt-8"><SiteInfoManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
